<script async setup lang="ts">
import { Carousel, Slide, Navigation } from 'vue3-carousel';

import { EventEntity } from '@/entities/interfaces/events';
import { useViewStore } from '@/stores/view.store';
import { useEventStore } from '@/stores/events.store';
import { computed, onMounted, ref, watch } from 'vue';
import { getPhotos } from '@/entities/api/photos';

import PhotoPreview from '../preview/PhotoPreview.vue';
import { PhotoEntity } from '@/entities/interfaces/photos';

const viewStore = useViewStore();
const view = viewStore.getViewType;

const eventStore = useEventStore();
const prevEvents = computed<EventEntity[]>(
  () => eventStore.getEventsTimeline.value?.prevEvents
);

const photos = ref<PhotoEntity[]>();

const setGallery = () => {
  if (!prevEvents.value) return;

  Promise.all(
    prevEvents.value?.map((item) => {
      return getPhotos(item.id).then((res) => {
        return (
          res.find((i) => {
            return i.id === item.mainGalleryImage;
          }) ?? res[0]
        );
      });
    })
  ).then((res) => (photos.value = res.filter((i) => i != undefined)));
};

watch(prevEvents, () => {
  setGallery();
});

onMounted(() => {
  setGallery();
});
</script>

<template>
  <section class="gallery" v-if="photos?.length">
    <div class="gallery-title">
      <h1>Фотогалерея</h1>
    </div>
    <div class="gallery-list">
      <Carousel
        v-if="photos?.length"
        :items-to-show="view == 'mobile' ? 1 : view == 'desktop' ? 2 : 3"
        style="width: 100%"
        snapAlign="start"
      >
        <Slide
          v-for="photo in photos"
          :key="photo.id"
          style="width: 100%"
          :style="
            view == 'large'
              ? 'width: 440px;  margin: 0;'
              : view == 'desktop'
                ? 'width: calc(50%); margin: 0;'
                : ''
          "
        >
          <PhotoPreview :photo="photo" />
        </Slide>

        <template #addons>
          <Navigation
            v-if="
              (view != 'mobile' && photos.length > 2) ||
              (view == 'mobile' && photos.length > 1)
            "
          />
        </template>
      </Carousel>
    </div>
  </section>
</template>

<style scoped lang="scss">
.gallery {
  width: 100%;
  margin-bottom: 148px;

  &-title {
    gap: 56px;
    padding: 80px 0 40px;

    @media screen and (max-width: 1024px) {
      text-align: center;
    }
  }

  &-list {
    gap: 40px;
    display: flex;
    flex-wrap: wrap;
    padding: 60px 0 0;
  }
}

.photo {
  width: 100%;
  margin: 12px;
  display: flex;
  height: 385px;
  flex: 1 440px;
  max-width: 676px;
  overflow: hidden;
  align-items: center;
  border-radius: 40px;
  position: relative;
  justify-content: center;
  flex-direction: column;
  background: var(--color-mono-10);

  &:hover {
    .event-image {
      img {
        transform: scale(1.1);
      }

      .filter {
        opacity: 0.2;
      }
    }
  }

  &-image {
    width: 100%;
    height: 400px;
    overflow: hidden;
    position: relative;
    border-radius: 40px 40px 0 0;

    display: flex;
    align-items: center;
    justify-content: center;

    .filter {
      top: 0;
      left: 0;
      opacity: 0;
      z-index: 1;
      width: 100%;
      height: 100%;
      position: absolute;
      transition: 0.3s ease-in-out;
      background: var(--color-mono-900);
    }

    img {
      min-width: 100%;
      min-height: 100%;
      object-fit: cover;
      transition: 0.3s ease-in-out;
    }
  }

  &-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;

    min-height: 120px;
    max-height: 120px;
    height: 120px;
    padding: 24px;

    p {
      text-align: left;
      color: var(--color-mono-900);
      font-size: var(--font-size-m);
      line-height: 20px;
    }

    time {
      font-weight: var(--font-weight-light);
      color: var(--color-mono-400);
      font-size: var(--font-size-xs);
    }
  }
}
</style>
